import { faCheck, faPlus, faTrash, faUserPlus } from "@fortawesome/pro-regular-svg-icons";

import { Node, Handle, NodeProps, Position } from "@xyflow/react";
import { FunctionComponent, useMemo, useState } from "react";

import { format } from "date-fns";

import { Icon, Modal } from "@/components";

import { InputField } from "@/components/InputField/InputField";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { listSelector } from "@/pages/Private/redux/list/list.slice";

import { useAppSelector } from "@/redux/hooks";

import { classNames } from "@/utils/classNames";

import { Information } from "@/components/Information/Information";

import { collectNodeMessageContents } from "@/pages/Private/pages/Messages/components/Variables/utils/variableRows";

import { getSuggestions } from "../../../../utils/column-utils";
import { replacePlaceholders } from "../../utils/replacePlaceholders";
import { useNormalizedCompanyName } from "../../utils/useNormalizedCompanyName";
import { NodeData } from "../../pages/CampaignPage";
import { SentDetailsTooltip } from "./SentDetailsTooltip";
import { StepFailureNote } from "./StepFailureNote";
import { StepPathWarningNote } from "./StepPathWarningNote";

export type LinkedinInviteNodeConfig = Node<NodeData, "linkedinInviteNode">;

export const LinkedinInviteNode: FunctionComponent<NodeProps<LinkedinInviteNodeConfig>> = (
	props
) => {
	const { inbox, currentLeadIndex, currentList } = useAppSelector(listSelector);

	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

	const { data, selected } = props;

	const previousNode = data.getPreviousNode?.(props.id);
	const childrenNodes = data.getChildrenNodes?.(props.id) || [];

	const currentType = useMemo(() => {
		return previousNode?.type || "starterNode";
	}, [previousNode]);

	const suggestions = useMemo(() => {
		return inbox[currentLeadIndex]
			? getSuggestions(inbox[currentLeadIndex], undefined, undefined, currentList?.event)
			: [];
	}, [currentLeadIndex, inbox, currentList?.event]);

	// Resolve {prospect_organization_nameNormalized} up front so the card previews the same company
	// name the send will use, instead of the raw one it falls back to (PREC-3923).
	const previewLead = useNormalizedCompanyName(
		inbox[currentLeadIndex],
		collectNodeMessageContents(data)
	);

	const hasNextNode = useMemo(() => {
		return childrenNodes.length >= 1 || false;
	}, [childrenNodes]);

	return (
		<div
			className={classNames(
				"rounded-lg border bg-white h-auto relative w-[350px]",
				data.isDisconnectedTarget ? "border-status-error border-2" : "",
				data.alreadySent && !data.failed && !data.isDisconnectedTarget
					? "border-status-success"
					: "",
				data.failed && !data.isDisconnectedTarget ? "border-status-error" : "",
				data.scheduledFor && !data.isDisconnectedTarget ? "border-status-warning" : "",
				!data.isDisconnectedTarget && selected ? "border-blue-action" : "",
				!data.isDisconnectedTarget && !selected ? "border-gray-200" : ""
			)}
		>
			<div className="text-xs h-auto w-full bg-action-200 text-text-meta px-2  py-1 rounded-t-lg flex flex-col gap-1">
				<div className="flex flex-row justify-between items-center">
					<span className="text-sm">Step {data.level} - Send LinkedIn Invite</span>
					<div className="flex items-center justify-center gap-2">
						<Icon
							className="w-6 h-6 border border-status-success text-status-success bg-status-success-light rounded-full p-1"
							icon={faCheck}
						/>
						{data.deleteNode && !data.alreadySent && (
							<div
								className="flex items-center justify-center cursor-pointer"
								onClick={(e) => {
									e.stopPropagation();
									e.preventDefault();
									setShowDeleteModal(true);
								}}
							>
								<Icon
									className="w-6 h-6 border border-status-error text-status-error bg-status-error-light rounded-full p-1 hover:text-status-error-light hover:bg-status-error"
									icon={faTrash}
								/>
							</div>
						)}
					</div>
				</div>
				<div className="flex flex-row justify-between">
					<div className="w-auto top-0 right-0 flex flex-row items-center justify-end gap-1">
						{currentType !== "starterNode" && !data.alreadySent ? (
							<>
								<span className="text-text-meta text-sm">after</span>{" "}
								<div className="w-[60px]">
									<InputField
										className="-mb-3 h-[20px]"
										handleChange={(e) => {
											if (
												isNaN(Number(e.target.value)) ||
												e.target.value?.length > 2 ||
												Number(e.target.value) < 0 ||
												Number(e.target.value) > 30
											) {
												return;
											}

											data?.handleSetDays?.(props.id, Number(e.target.value));
										}}
										max={30}
										min={0}
										name="days"
										pattern="[0-9]{2}"
										placeholder="days"
										size={ButtonSize.XS}
										type="text"
										value={data.days || 0}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
										}}
									/>
								</div>
								<span className="text-text-meta text-sm">
									{(data.days || 0) > 1 || !data.days || data.days === 0 ? "days" : "day"}{" "}
									<Information information="The number of days in which the event must occur. If you set 0, the execution of this node will happen earliest 10 minutes after the previous one. " />
								</span>{" "}
							</>
						) : (
							<span className="text-text-meta"></span>
						)}
						{data.failed && <StepFailureNote failureCategory={data.failureCategory} />}
						{data.alreadySent && !data.failed && (
							<SentDetailsTooltip sentAt={data.sentAt} sentByUserName={data.sentByUserName}>
								<div className="text-text-meta text-xs cursor-help">Invitation sent</div>
							</SentDetailsTooltip>
						)}
						{!!data.scheduledFor && (
							<div className="text-text-meta text-xs">
								Scheduled for {format(new Date(data.scheduledFor || ""), "HH:mm dd.MM.yyyy")}
							</div>
						)}
					</div>
				</div>
			</div>

			{!data.alreadySent && <StepPathWarningNote nodeId={props.id} stepType={props.type} />}

			<div className="text-sm h-auto w-full text-text-meta px-2  py-1 flex items-start gap-1 w-full">
				<Icon
					className={classNames(data.message ? "text-status-success" : "text-status-info", "mt-1")}
					icon={faUserPlus}
				/>
				<div className="flex flex-col gap-1 w-full">
					{data.message ? (
						<>
							<div className="text-sm whitespace-nowrap flex flex-row gap-1 items-center mr-4">
								Normal User:
							</div>
							<div
								dangerouslySetInnerHTML={{
									__html: replacePlaceholders(
										data.message,
										previewLead,
										suggestions,
										currentList?.event
									),
								}}
								className="inline max-h-[38px] nowheel w-[calc(100%-24px)] overflow-x-hidden text-left"
								onScroll={(e) => {
									e.stopPropagation();
								}}
								onWheel={(e) => {
									e.stopPropagation();
								}}
							></div>
						</>
					) : (
						<div className="text-status-info flex gap-0 flex-col items-start">
							<div className="text-sm whitespace-nowrap flex flex-row gap-1 items-center mr-4">
								Normal User:
							</div>
							<div className="text-sm whitespace-nowrap flex flex-row gap-1 items-center mr-4">
								Connection request message not set{" "}
								<Information information="You don’t need to send a connection request message and can leave this empty" />
							</div>
						</div>
					)}

					{data.premiumMessage ? (
						<>
							<span className="text-sm whitespace-nowrap flex flex-row gap-1 items-center mr-4">
								Premium User:
							</span>
							<div
								dangerouslySetInnerHTML={{
									__html: replacePlaceholders(
										data.premiumMessage,
										previewLead,
										suggestions,
										currentList?.event
									),
								}}
								className="inline max-h-[38px] nowheel w-[calc(100%-24px)] overflow-x-hidden text-left"
								onScroll={(e) => {
									e.stopPropagation();
								}}
								onWheel={(e) => {
									e.stopPropagation();
								}}
							></div>
						</>
					) : (
						<div className="text-status-info flex gap-0 flex-col items-start">
							<div className="text-sm whitespace-nowrap flex flex-row gap-1 items-center mr-4">
								Premium User:
							</div>
							<div className="text-sm whitespace-nowrap flex flex-row gap-1 items-center mr-4">
								Connection request message not set{" "}
								<Information information="You don’t need to send a premium connection request message and can leave this empty" />
							</div>
						</div>
					)}
				</div>
			</div>

			<div className="z-[100]">
				<Handle
					position={Position.Top}
					style={
						data.isDisconnectedTarget
							? {
									visibility: "visible",
									width: 12,
									height: 12,
									background: "#ef4444",
									border: "2px solid #fff",
									top: -6,
								}
							: { visibility: "hidden" }
					}
					type="target"
				/>
				<Handle
					id="normalEdge"
					position={Position.Bottom}
					style={
						data.hasOpenSource
							? {
									visibility: "visible",
									width: 12,
									height: 12,
									background: "#ef4444",
									border: "2px solid #fff",
									bottom: -6,
								}
							: { visibility: "hidden" }
					}
					type="source"
				/>
			</div>
			{!data.alreadySent && !data.scheduledFor && data?.handleAddStep && (
				<div
					className={classNames(
						"absolute bottom-[-55px] left-[50%] translate-x-[-50%] h-[55px] w-[1px] bg-text-meta opacity-50 z-[90]",
						hasNextNode ? "bg-text-disabled hidden" : "bg-text-meta block"
					)}
				></div>
			)}

			{!data.alreadySent && !data.scheduledFor && data?.handleAddStep && (
				<div
					className="bg-white shadow-md border border-border-light w-6 h-6 text-text-disabled p-2 rounded-full absolute bottom-[-55px] left-[50%] translate-x-[-50%] flex items-center justify-center z-[100]"
					onClick={(e) => {
						e.stopPropagation();
						e.preventDefault();

						data?.handleAddStep?.({
							position: { x: props.positionAbsoluteX, y: props.positionAbsoluteY },
							initiatorId: props.id,
							edgeType: "normalEdge",
							nodeType: "linkedinInviteNode",
							hasNextNode: hasNextNode,
						});
					}}
				>
					<Icon className="w-4 h-4" icon={faPlus} />
				</div>
			)}
			{showDeleteModal && (
				<Modal
					handleClose={() => setShowDeleteModal(false)}
					handleSave={() => {
						data.deleteNode?.(props.id);
						setShowDeleteModal(false);
					}}
					isOpened={showDeleteModal}
					submitButtonColor={ButtonColor.DANGER}
					submitButtonText="Delete"
					title="Delete node"
				>
					<div className="text-sm">Are you sure you want to delete this node?</div>
				</Modal>
			)}
		</div>
	);
};
